/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/ReviewAppointmentModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger } from "../../utils/logger.js";
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { CircularProgress } from "@mui/material";
import styles from "./index.module.css";

const ReviewAppointmentModal = ({
  open,
  onClose,
  appointment,
  onConfirmReview,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dialogRef = useRef(null);

  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);

  const handleSubmit = () => {
    if (!onConfirmReview || isSubmitting) {
      return;
    }
    if (!appointment?._id) {
      logger.error("Review appointment modal missing context");
      return;
    }
    onConfirmReview(rating);
  };

  useEffect(() => {
    const dialog = dialogRef.current;

    const handleClose = () => onClose();

    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      setRating(
        appointment?.calificacionCita ? appointment.calificacionCita : 0,
      );
      dialog.showModal();
    } else {
      dialog.close();
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onClose]);

  return (
    <>
      {open && <div className={styles.overlay}></div>}

      <dialog ref={dialogRef} className={styles.modal}>
        {/* Close button */}
        <div className={styles.closeButtonContainer}>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          <label className={styles.title}>
            {appointment?.nombreUsuarioRemitente || "—"}
          </label>

          <span className={styles.subtitle}>
            {t("components.reviewAppointmentModal.subtitle")}
          </span>

          <span className={styles.description}>
            {t("components.reviewAppointmentModal.description")}
          </span>

          {/* ⭐ Stars */}
          <div className={styles.starsContainer}>
            {[1, 2, 3, 4, 5].map((star) => (
              <span
                key={star}
                className={`${styles.star} ${
                  (hovered || rating) >= star ? styles.filled : ""
                }`}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHovered(star)}
                onMouseLeave={() => setHovered(0)}
              >
                ★
              </span>
            ))}
          </div>
        </div>

        {/* Buttons */}
        <div className={styles.buttonsContainer}>
          <button onClick={onClose} className={styles.cancelButton}>
            {t("components.reviewAppointmentModal.cancelButton")}
          </button>

          <button
            className={styles.primaryButton}
            onClick={handleSubmit}
            disabled={!rating}
          >
            ⭐ {t("components.reviewAppointmentModal.submitButton")}
          </button>
        </div>
      </dialog>
    </>
  );
};

export default ReviewAppointmentModal;
